<template>
  <div class="card">
    <header class="header-card">
      <p>
        {{ $t('catalogue.newProduct.ecransAssociees') }}
      </p>
    </header>
    <div class="card-content">
      <div class="columns">
        <div class="column is-4">
          <form @submit.prevent.stop="filter = filterInput">
            <div v-if="ecranList.length"
                 class="field has-addons">
              <div class="control inputSearch has-icons-left is-clearfix">
                <b-input :placeholder="$t('catalogue.actions.searchEcran')"
                         v-model="filterInput" type="search">
                </b-input>
                <b-icon class="icon is-left"
                        icon="magnify"></b-icon>
              </div>
              <p class="control">
                <button class="button is-primary"
                        type="submit">
                  {{ $t('general.button.search') }}
                </button>
              </p>
            </div>
          </form>
        </div>
        <div class="column is-2">
          <b-datepicker v-if="ecranList.length"
                        show-week-number
                        nearby-selectable-month-days
                        icon="calendar-today"
                        v-model="dateVisualisation"
                        :editable="true">
          </b-datepicker>
        </div>
        <div class="column is-4 is-offset-2">
          <form @submit.prevent.stop="ajouterEcran">
            <div class="is-pulled-right field">
              <div class="control inputSearch is-clearfix">
                <b-autocomplete
                  class="dropdown is-bottom-left is-block"
                  v-model="critereRecherche"
                  :open-on-focus="false"
                  :data="autocompleteEcranList"
                  field="label"
                  :loading="isFetching"
                  @select="ecran => selectEcran(ecran)"
                  @input="getAsyncEcranData"
                  @keyup.native.enter="onPressEnter"
                  :placeholder="$t('catalogue.actions.ajouterEcran')">
                </b-autocomplete>
              </div>
            </div>
          </form>
        </div>
      </div>
      <template v-if="ecranList.length">
        <div class="is-pulled-right">
          <exportExcelButton
            :perm="permCatalogue.exporter"
            in-progress/>
        </div>
        <selecteur-action
          @handleAction="handleAction"
          :elementsSelectionnes="isEcranSelected"
          :titleButton="'general.button.apply'"
          :options='actions'
          :placeholder='$t("general.placeHolder.groupedAction")'
        ></selecteur-action>
        <v-client-table :data="filteredEcranList"
                        class='mgt-10px column is-12 is-paddingless'
                        :columns="columns"
                        :options="options">
          <div slot="selected" slot-scope="props" class="has-text-centered">
            <qd-checkbox v-model="ecranList[indexOfEcranNumerique(props.row)].selected">
            </qd-checkbox>
          </div>
          <div slot="produit.reference" slot-scope="props">
            <router-link-blank
              :to="{ name: 'c.bodac.catalogue-ecranNumerique',
                params: { id: props.row.produit.id } }"
              :message="props.row.produit.reference"></router-link-blank>
          </div>
          <div slot="outdoor" slot-scope="props" class="has-text-centered">
            <qd-checkbox :value="props.row.produit.produitEcranNumerique
            ? props.row.produit.produitEcranNumerique.outdoor : false"
                        type="is-default"
                        class="pointer-events-none">
            </qd-checkbox>
          </div>
          <div slot="startDate" slot-scope="props">
            <b-datepicker show-week-number
                          nearby-selectable-month-days
                          :class="{'is-danger':
                startDateHasError(ecranList[indexOfEcranNumerique(props.row)])}"
                          v-model="ecranList[indexOfEcranNumerique(props.row)].dateDebut"
                          position="is-bottom-left"
                          :editable="true">
            </b-datepicker>
          </div>
          <div slot="endDate" slot-scope="props">
            <b-datepicker show-week-number :class="{'is-danger':
            dateHasError(ecranList[indexOfEcranNumerique(props.row)])}"
                          v-model="ecranList[indexOfEcranNumerique(props.row)].dateFin"
                          :min-date="ecranList[indexOfEcranNumerique(props.row)].dateDebut"
                          position="is-bottom-left"
                          nearby-selectable-month-days
                          :editable="true">
            </b-datepicker>
          </div>
          <div slot="supprimer" slot-scope="props" class="has-text-centered">
            <a @click="supprimerEcranNumerique(props.row)">
              <b-icon
                icon="delete-forever"
                type="is-danger">
              </b-icon>
            </a>
          </div>
        </v-client-table>
      </template>
    </div>
  </div>
</template>

<script>
import cloneDeep from 'lodash/cloneDeep';
import moment from 'moment';
import genererGuid from '@/services/general/genererGuid';
import {
  TEXTS, RESULTS_PER_PAGE, NO_PER_PAGE_VALUE, SORT_ICON,
} from '@/configuration/clientTableConf';
import { getAll } from '@/api/produit/produitNumerique';
import { CATALOGUE } from '@/configuration/permissionsBodacConf';
import messageErreur from '@/services/general/messageErreur';
import routerLinkBlank from '@/components/general/routerLinkBlank';
import formatDate from '@/services/general/formatDate';
import { INFINITE_DAY } from '@/configuration/general';
import selecteurAction from '@/components/general/selecteurAction';
import exportExcelButton from '@/components/general/button/exportExcelButton';
import { ACTION_DELETE } from '@/configuration/general/actionsConf';

export default {
  props: {
    produit: {
      type: Object,
      required: true,
    },
  },
  components: {
    routerLinkBlank,
    selecteurAction,
    exportExcelButton,
  },
  data() {
    return {
      critereRecherche: '',
      ecranList: [],
      autocompleteEcranList: [],
      selectedEcran: null,
      isFetching: false,
      filter: '',
      filterInput: '',
      permCatalogue: CATALOGUE,
      dateVisualisation: new Date(),
      actions: [
        { action: ACTION_DELETE, translation: 'catalogue.actions.delete' },
      ],
    };
  },
  computed: {
    columns() {
      return [
        'selected',
        'produit.reference',
        'produit.produitEcranNumerique.format',
        'produit.produitEcranNumerique.materielType',
        'adresse',
        'produit.produitEcranNumerique.adresse.ville',
        'produit.produitEcranNumerique.adresse.codePostal',
        'outdoor',
        'startDate',
        'endDate',
        'supprimer'];
    },
    iconSelectTotal() {
      if (this.areAllSelected) {
        return 'checkbox-multiple-marked-outline';
      }
      return 'checkbox-multiple-blank-outline';
    },
    options() {
      const texts = cloneDeep(TEXTS);
      texts.count = '';
      return {
        headings: {
          'produit.reference': this.$i18n.t('catalogue.table.ecran'),
          'produit.produitEcranNumerique.format': this.$i18n.t('catalogue.table.format'),
          'produit.produitEcranNumerique.materielType': this.$i18n.t('catalogue.table.materiel'),
          adresse: this.$i18n.t('catalogue.table.adresse'),
          'produit.produitEcranNumerique.adresse.ville': this.$i18n.t('catalogue.table.ville'),
          'produit.produitEcranNumerique.adresse.codePostal': this.$i18n.t('catalogue.table.codePostal'),
          startDate: this.$i18n.t('catalogue.table.startDate'),
          endDate: this.$i18n.t('catalogue.table.endDate'),
          outdoor: this.$i18n.t('catalogue.table.outdoor'),
          supprimer: '',
          selected: (h) => {
            return h(
              'a', {
                attrs: {
                  class: 'pointer',
                },
                on: {
                  click: () => {
                    const allSelected = this.areAllSelected;
                    this.filteredEcranList.forEach(item => {
                      item.selected = !allSelected;
                    });
                  },
                },
              },
              [
                h('b-icon', {
                  attrs: {
                    icon: this.iconSelectTotal,
                  },
                }),
              ],
            );
          },
        },
        columnsClasses: {
          selected: 'colonneSelected',
          adresse: 'ellipsis address',
          startDate: 'date',
          endDate: 'date',
        },
        filterable: false,
        perPageValues: NO_PER_PAGE_VALUE,
        perPage: RESULTS_PER_PAGE,
        texts: texts,
        sortIcon: SORT_ICON,
        sortable: [
          'produit.reference',
          'produit.produitEcranNumerique.format',
          'produit.produitEcranNumerique.materielType',
          'adresse',
          'produit.produitEcranNumerique.adresse.codePostal',
          'produit.produitEcranNumerique.adresse.ville',
        ],
      };
    },
    isEcranSelected() {
      return this.ecranList.some(item => item.selected);
    },
    areAllSelected() {
      return this.filteredEcranList.length && !this.filteredEcranList.some(item => !item.selected);
    },
    filteredEcranList() {
      this.ecranList.forEach((ecran) => {
        ecran.selected = false;
      });
      return this.ecranList.filter(item => {
        const produitSearch = [
          item.produit.produitEcranNumerique.codeEcran,
          item.produit.produitEcranNumerique.format,
          item.produit.produitEcranNumerique.materielType,
          item.produit.produitEcranNumerique.adresse.adresse1,
          item.produit.produitEcranNumerique.adresse.adresse2,
          item.produit.produitEcranNumerique.adresse.adresse3,
          item.produit.produitEcranNumerique.adresse.codePostal,
          item.produit.produitEcranNumerique.adresse.ville,
        ];
        let find = false;
        const dateFin = item.dateFin ? moment(item.dateFin) : moment(INFINITE_DAY);
        if (!this.dateVisualisation
          || moment(this.dateVisualisation).isBetween(moment(item.dateDebut),
            dateFin, 'day', '[]')) {
          produitSearch.forEach((produitSearch) => {
            if (produitSearch && produitSearch.toLowerCase().includes(this.filter.toLowerCase())) {
              find = true;
            }
          });
        }
        return find;
      });
    },
  },
  mounted() {
    this.ecranList = this.produit.produitReseauNumerique.affectationsReseauEcran;
    this.ecranList.forEach(item => {
      item.guid = genererGuid();
      this.$set(item, 'selected', false);
      if (item.dateDebut) item.dateDebut = new Date(item.dateDebut);
      if (item.dateFin) item.dateFin = new Date(item.dateFin);
      item.adresse = this.getAddressLabel(item.produit.produitEcranNumerique);
    });
  },
  methods: {
    async getAsyncEcranData() {
      this.autocompleteEcranList = [];
      try {
        if (this.critereRecherche && this.critereRecherche.length < 3) return;
        this.isFetching = true;
        const result = {
          query: {
            references: [this.critereRecherche],
            societeId: this.produit.supportCommercial.societeId,
          },
        };
        this.autocompleteEcranList = (await getAll(result)).data.result.items;
        this.isFetching = false;

        this.autocompleteEcranList.forEach(item => {
          const ecranNumerique = item.produitEcranNumerique;
          item.label = item.reference + ' - ';
          item.label += ecranNumerique.adresse.adresse1 ? ecranNumerique.adresse.adresse1 : '';
          item.label += ecranNumerique.adresse.codePostal && ecranNumerique.adresse.adresse1 ? ' ' : '';
          item.label += ecranNumerique.adresse.codePostal ? ecranNumerique.adresse.codePostal : '';
          item.label += ecranNumerique.adresse.adresse1 || ecranNumerique.adresse.codePostal ? ' - ' : '';
          item.label += ecranNumerique.adresse.ville;
          item.selected = false;
        });
      } catch (e) {
        messageErreur(this.$i18n);
        throw e;
      }
    },
    selectEcran(ecran) {
      if (ecran) {
        this.selectedEcran = ecran;
        this.ajouterEcran();
      }
    },
    ajouterEcran() {
      if (this.selectedEcran) {
        const item = {};
        item.produit = this.selectedEcran;
        item.selected = false;
        item.dateDebut = new Date();
        item.dateFin = null;
        item.guid = genererGuid();
        item.adresse = this.getAddressLabel(item.produit.produitEcranNumerique);
        this.produit.produitReseauNumerique.affectationsReseauEcran.unshift(item);
        this.critereRecherche = null;
        this.selectedEcran = null;
      }
    },
    supprimerEcranNumerique(ecran) {
      const index = this.produit.produitReseauNumerique
        .affectationsReseauEcran.findIndex((affectation) => {
          return affectation.produit.id === ecran.produit.id
            && formatDate(affectation.dateDebut) === formatDate(ecran.dateDebut)
            && formatDate(affectation.dateFin) === formatDate(ecran.dateFin);
        });
      if (index > -1) this.produit.produitReseauNumerique.affectationsReseauEcran.splice(index, 1);
    },
    supprimerMultiEcranNumerique() {
      for (let i = 0; i
      < this.produit.produitReseauNumerique.affectationsReseauEcran.length; i += 1) {
        if (this.produit.produitReseauNumerique.affectationsReseauEcran[i].selected) {
          this.produit.produitReseauNumerique.affectationsReseauEcran.splice(i, 1);
          i -= 1;
        }
      }
    },
    getAddressLabel(ecran) {
      let address = '';
      address = ecran.adresse.adresse1;
      if (ecran.adresse.adresse2) {
        address += `, ${ecran.adresse.adresse2}`;
      }
      if (ecran.adresse.adresse3) {
        address += `, ${ecran.adresse.adresse3}`;
      }
      return address;
    },
    indexOfEcranNumerique(ecran) {
      const item = this.ecranList.find(item => item.guid === ecran.guid);
      return this.ecranList.indexOf(item);
    },
    dateHasError(ecran) {
      return this.$parent.error && moment(ecran.dateDebut).isAfter(moment(ecran.dateFin));
    },
    startDateHasError(item) {
      return !item.dateDebut && this.$parent.error;
    },
    onPressEnter() {
      if (this.selectedEcran) {
        this.ajouterEcran();
      }
    },
    handleAction(action) {
      if (action === ACTION_DELETE) this.supprimerMultiEcranNumerique();
    },
  },
};

</script>
